import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { init as initExcel, addGame as addGame2Excel, download as downloadExcel } from '../FormalGame/xlsx';
import Button from '~~elements/Button';
import Multilingual from '~~utils/Multilingual';
import moment from 'moment';
import ScorebookButton from '../FormalTournament/ScorebookButton';
import { downloadOpenData, toOpenData } from './json';

const Div = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
  .score-board {
    display: flex;
    max-width: 80%;
    overflow-x: auto;
    text-align: center;
    padding: 0 0 1rem;
    .column {
      width: 2rem;
      border-right: 1px solid #eee;
      &:last-child {
        border-right: 0;
      }

      .cell {
        width: 100%;
        height: 1.6rem;
        display: block;
        border-bottom: 1px solid #eee;
        &.header {
          font-weight: 600;
        }
        &:last-child {
          border-bottom: 0;
        }
      }
    }
  }
  .basic-info {
    display: grid;
    grid-template-columns: 1fr 2fr;
    > * {
      border-bottom: 1px solid #eee;
      margin: 0;
      padding: 0.25rem 0.5rem;
      &.row-gap {
        margin-bottom: 1rem;
      }
    }
  }
`;

const FormalGameOverview = ({ tournamentInfo, isPlatformAdmin, game }) => {
  const [downloading, setDownloading] = useState(false);
  const handlePitchesDownload = () => {
    setDownloading(true);
    const title = `G${game.seq}-${game.uniqid}-逐球數據`;
    const sheets = initExcel();
    addGame2Excel(game, sheets);
    downloadExcel(title, sheets);
    setDownloading(false);
  };

  const handleJsonDownload = () => {
    setDownloading(true);
    const opendData = toOpenData(game);
    downloadOpenData(`${game.season.title}-G${game.seq}`, opendData);
    setDownloading(false);
  };

  const getTeamScoreSection = () => {
    const awayTeamName = <label>{game.away.team}</label>;
    const homeTeamName = <label>{game.home.team}</label>;

    if (game.info.status !== 'FINISHED') {
      return (
        <h4>
          {awayTeamName}
          <label>-:-</label>
          {homeTeamName}
        </h4>
      );
    }

    return (
      <h4>
        {awayTeamName}
        <label>{`${game.away.runs}:${game.home.runs}`}</label>
        {homeTeamName}
      </h4>
    );
  };

  const getScoreBoardSection = () => {
    const scoreboardColumns = [
      ['R', '-', '-'],
      ['H', '-', '-'],
      ['E', '-', '-'],
    ];
    if (game.info.status !== 'FINISHED') {
      scoreboardColumns.unshift(['-', '-', '-']);
    } else {
      scoreboardColumns[0][1] = game.away.runs;
      scoreboardColumns[0][2] = game.home.runs;
      scoreboardColumns[1][1] = game.away.hits;
      scoreboardColumns[1][2] = game.home.hits;
      scoreboardColumns[2][1] = game.away.errors;
      scoreboardColumns[2][2] = game.home.errors;
      for (let inning = 0; inning < game.info.innings; inning++) {
        scoreboardColumns.splice(inning, 0, [
          (inning + 1).toString(),
          !game.away.scores[inning] ? '-' : game.away.scores[inning],
          !game.home.scores[inning] ? '-' : game.home.scores[inning],
        ]);
      }
    }

    return (
      <div className="score-board">
        {scoreboardColumns.map((scoreboardColumn, i) => (
          <div key={i} className="column">
            <span className="cell header">{scoreboardColumn[0]}</span>
            <span className="cell">{scoreboardColumn[1]}</span>
            <span className="cell">{scoreboardColumn[2]}</span>
          </div>
        ))}
      </div>
    );
  };

  const getFinishedGameInfo = () => {
    const finishedStatusFragment = (
      <Fragment>
        <h4>{Multilingual(`GAME.FINISHED_STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.FINISHED_STATUS.${game.info.finished_status}`)}</label>
      </Fragment>
    );

    if (game.info.finished_status === 'SUSPENDED') {
      return finishedStatusFragment;
    }

    let matchResultWording = `${game.away.runs}:${game.home.runs} `;
    switch (game.info.winner_side) {
      case 'AWAY':
        matchResultWording += `- ${game.away.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'HOME':
        matchResultWording += `- ${game.home.team} ${Multilingual(`GAME.MATCH_RESULT.WIN`)}`;
        break;
      case 'TIE':
        matchResultWording += Multilingual(`GAME.MATCH_RESULT.DRAW`);
        break;
      default:
        return finishedStatusFragment;
    }

    return (
      <Fragment>
        {finishedStatusFragment}
        <h4>{Multilingual(`GAME.MATCH_RESULT.WORDING`)}</h4>
        <label>{matchResultWording}</label>
      </Fragment>
    );
  };

  return (
    <Div>
      {getTeamScoreSection()}
      {getScoreBoardSection()}
      <div className="basic-info">
        <h4>{Multilingual(`GAME.AWAY`)}</h4>
        <label>{game.away.team}</label>
        <h4 className="row-gap">{Multilingual(`GAME.HOME`)}</h4>
        <label className="row-gap">{game.home.team}</label>
        <h4>{Multilingual(`GAME.STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.STATUS.${game.info.status}`)}</label>
        {game.info.status === 'FINISHED' && getFinishedGameInfo()}
        <h4>{Multilingual(`GAME.SCHEDULED_START_AT`)}</h4>
        <label>
          {moment(game.info.scheduled_start_at).isValid()
            ? moment(game.info.scheduled_start_at).format('YYYY/MM/DD HH:mm')
            : '-'}
        </label>
        <h4>{Multilingual(`GAME.STARTED_AT`)}</h4>
        <label>
          {moment(game.info.started_at).isValid() ? moment(game.info.started_at).format('YYYY/MM/DD HH:mm') : '-'}
        </label>
        <h4 className="row-gap">{Multilingual(`GAME.ENDED_AT`)}</h4>
        <label className="row-gap">
          {moment(game.info.ended_at).isValid() ? moment(game.info.ended_at).format('YYYY/MM/DD HH:mm') : '-'}
        </label>
        <h4>{Multilingual(`GAME.LOCATION`)}</h4>
        <label>{game.info.location}</label>
        <h4>{Multilingual(`GAME.STATS_COMPLETENESS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.STATS_COMPLETENESS.${game.info.stats_completeness}`)}</label>
        <h4>{Multilingual(`GAME.STATS_STATUS.WORDING`)}</h4>
        <label>{Multilingual(`GAME.STATS_STATUS.${game.info.stats_status}`)}</label>
        {isPlatformAdmin && (
          <>
            <h4 className="row-gap">{Multilingual(`GAME.DATA_EXPORT.WORDING`)}</h4>
            <Button className="row-gap" onClick={handlePitchesDownload} disabled={downloading}>{`${Multilingual(
              `GAME.DATA_EXPORT.PBP`,
            )} Excel`}</Button>
            <h4 className="row-gap">{Multilingual(`GAME.DATA_EXPORT.WORDING`)}</h4>
            <Button className="row-gap" onClick={handleJsonDownload} disabled={downloading}>
              OpenData Json
            </Button>
            <h4 className="row-gap">同步至 SCOREBOOK</h4>
            <ScorebookButton className="row-gap" tournament={tournamentInfo} games={[game]} />
          </>
        )}
      </div>
    </Div>
  );
};

export default FormalGameOverview;
